// ABOUTME: Shared TypeScript types for the Country Configurations feature.
// ABOUTME: Includes types for detail layout, header/footer props, entity/scope models, and field-level inheritance.

import type {ReactNode} from 'react'

// --- Header & Layout types ---

/** Summary status of values on a scope — displayed as a pill in the content header */
export type HeaderValuesStatus = 'overridden' | 'allInherited'

/** Information about the parent scope shown in the header subtitle */
export interface ParentScopeInfo {
  /** Display name of the parent scope — e.g. "Country default" */
  label: string
  /** Callback when the parent link is clicked — navigates to the parent scope */
  onClick?: () => void
}

export interface EntityContentHeaderProps {
  /** Name of the selected scope — e.g. "Sales org. 320", "Country default" */
  scopeTitle: string
  /** Parent scope info — omit for country-default variant (no subtitle shown) */
  parent?: ParentScopeInfo
  /** Summary values status — omit for country-default variant (no pill shown) */
  valuesStatus?: HeaderValuesStatus
  /** Callback when the reset/reload button is clicked (only shown for 'overridden') */
  onReset?: () => void
  /** Loading state — shows skeleton placeholder */
  isLoading?: boolean
  /** MDI icon path for the scope level — e.g. mdiClipboardTextClockOutline */
  scopeIcon?: string
  /** CSS class for the icon color — e.g. "text-accent_1" */
  scopeIconClassName?: string
  /** Extra action controls rendered on the right of the header (e.g. export/import). */
  actions?: ReactNode
}

export interface EntityContentFooterProps {
  /** Callback on Save click */
  onSave: () => void
  /** Callback on Discard click */
  onDiscard: () => void
  /** Whether the form has unsaved changes — controls button state and warning text */
  hasUnsavedChanges: boolean
  /** Whether data is still loading — hides footer entirely */
  isLoading?: boolean
  /** Whether save is in progress */
  isSaving?: boolean
  /** Whether the form has validation errors — disables Save even when dirty */
  hasErrors?: boolean
}

export interface EntityContentPanelProps {
  header: React.ReactNode
  footer: React.ReactNode
  children: React.ReactNode
  className?: string
}

export interface DetailLayoutProps {
  /** ScopeNavigator rendered in the left column */
  scopeNavigator: React.ReactNode
  /** EntityContentPanel rendered in the right column */
  children: React.ReactNode
}

// --- Fixture / data model types ---

export type ScopeLevel = 'country' | 'salesOrg' | 'dispatchGr' | 'dispatchCenter' | 'plant' | 'customer'

export const SCOPE_LEVEL_FILTER_ALL = 'all' as const
export type ScopeLevelFilterValue = ScopeLevel | typeof SCOPE_LEVEL_FILTER_ALL

/**
 * Maps frontend scope level to PascalCase API value.
 * Works for all endpoints (scope search, navigator, entity-catalog filter).
 */
export const LEVEL_TO_API: Record<ScopeLevel, string> = {
  country: 'Root',
  salesOrg: 'OrgUnit',
  dispatchGr: 'DispatchGroup',
  dispatchCenter: 'DispatchCenter',
  plant: 'Plant',
  customer: 'Customer',
}

const API_LEVEL_LOOKUP: Record<string, ScopeLevel> = {
  root: 'country',
  country: 'country',
  orgunit: 'salesOrg',
  dispatchgroup: 'dispatchGr',
  dispatchcenter: 'dispatchCenter',
  plant: 'plant',
  customer: 'customer',
}

/**
 * Maps API scope level string to frontend ScopeLevel.
 * Case-insensitive — handles both PascalCase ("OrgUnit") and camelCase ("orgUnit") API responses.
 */
export const apiToLevel = (apiLevel: string): ScopeLevel | undefined => {
  const normalized = apiLevel.toLowerCase()
  return API_LEVEL_LOOKUP[normalized]
}

/** Product context type — determines which entity groups are available */
export type ShippingType = 'bagged-deliver' | 'bagged-collect' | 'bulk-deliver' | 'bulk-collect'

// --- Entity catalog (GET /tuc-configs/entity-catalog) — HCP-108901 wrapper response ---

/** Reference to a unit of measure as returned in the entity-catalog `global`/`options` blocks. */
export interface UnitOfMeasureRef {
  id: string
  name: string
  displaySymbol: string
}

// --- Per-entity option groups (nested under each entity's `options`) ---

export interface QuantityLoadOptions {
  fullLoadsAvailable: boolean
  loadBalanceAvailable: boolean
}

export interface QuantityEntityOptions {
  availableUnitsOfMeasure?: UnitOfMeasureRef[]
  loadOptions?: QuantityLoadOptions
}

export interface RetarderEntityOptions {
  availableUnitsOfMeasure?: UnitOfMeasureRef[]
}

export interface TrucksEntityOptions {
  truckTypeSelectable?: boolean
  availableUnitsOfMeasure?: UnitOfMeasureRef[]
}

export interface LoadSpacingEntityOptions {
  stockpilingEnabled?: boolean
  availableUnitsOfMeasure?: {
    timeOnJobSite?: UnitOfMeasureRef[]
    spacing?: UnitOfMeasureRef[]
    flowRate?: UnitOfMeasureRef[]
  }
  scenarios?: {
    singleTruck?: { timeOnJobSite: boolean; spacing: boolean; flowRate: boolean }
    multiTruck?: { timeOnJobSite: boolean; spacing: boolean; flowRate: boolean }
  }
}

export interface TrucksEntityOptions {
  isTruckTypesListEnabled?: boolean
  availableUnitsOfMeasure?: UnitOfMeasureRef[]
}

export interface UnloadingMethodEntityOptions {
  allowedValues?: string[]
}

export interface LeadTimeOptionGroup {
  // N3: typed but intentionally unwired — no field-toggle UI concept exists yet (deferred).
  availableFields?: string[]
  availableReferenceEvents?: string[]
  availableUnitsOfMeasure?: UnitOfMeasureRef[]
  availableCalculationMethods?: string[]
}

export interface FarDistanceModifierOptionGroup {
  availableDistanceUnitsOfMeasure?: UnitOfMeasureRef[]
}

export interface OrderChangeLeadTimeEntityOptions {
  leadTime?: LeadTimeOptionGroup
}

export interface OrderIntakeLeadTimeEntityOptions {
  leadTime?: LeadTimeOptionGroup
  farDistanceModifier?: FarDistanceModifierOptionGroup
}

export interface OrderCancellationLeadTimeEntityOptions {
  leadTime?: LeadTimeOptionGroup
  farDistanceModifier?: FarDistanceModifierOptionGroup
}

export interface OverbookingAllowanceEntityOptions {
  availableUnitsOfMeasure?: UnitOfMeasureRef[]
  availableReferenceEvents?: string[]
}

export interface PumpSettingsEntityOptions {
  availableLeadTimeUnitsOfMeasure?: UnitOfMeasureRef[]
  availableSizeUnitsOfMeasure?: UnitOfMeasureRef[]
  availableHoseUnitsOfMeasure?: UnitOfMeasureRef[]
  availableReferenceEvents?: string[]
}

/**
 * Loose per-entity options bag carried on the catalog entry / Entity model. Each entity narrows
 * this to its specific option interface (e.g. `options as QuantityEntityOptions`) at the call site.
 */
export type EntityCatalogOptions = Record<string, unknown>

/** Single entity entry inside the entity-catalog `entities` array. */
export interface EntityCatalogEntryResponse {
  id: string
  name: string
  description: string
  applicableLevels: string[]
  /** Per-entity configurable options (nested groups). Absent when the backend omits them. */
  options?: EntityCatalogOptions
}

/** The always-present `global` block of the entity-catalog response. */
export interface EntityCatalogGlobal {
  referenceEvents: string[]
  calculationMethods: string[]
  dayCodes: string[]
  unitsOfMeasure: UnitOfMeasureRef[]
}

/** Wrapper response from GET /tuc-configs/entity-catalog (HCP-108901). */
export interface EntityCatalogResponse {
  global: EntityCatalogGlobal
  entities: EntityCatalogEntryResponse[]
}

/** Empty `global` used as a safe fallback (e.g. on 404) so consumers can always read its fields. */
export const EMPTY_ENTITY_CATALOG_GLOBAL: EntityCatalogGlobal = {
  referenceEvents: [],
  calculationMethods: [],
  dayCodes: [],
  unitsOfMeasure: [],
}

// --- Entity list (overview page) ---

/** Single entity returned by GET /entities */
export interface Entity {
  /** Unique identifier — used as route param when navigating to detail */
  id: string
  /** Display name — e.g. "Retarder", "Delivery window" */
  name: string
  /** Short description — e.g. "Configures limits and increments for retarders." */
  description: string
  /** Scope levels at which this entity can be configured */
  applicableLevels: ScopeLevel[]
  /** Per-entity configurable options from the entity catalog (HCP-108901). Undefined if omitted. */
  options?: EntityCatalogOptions
}

export type EntityType =
  | 'quantity'
  | 'retarder'
  | 'unloadingMethod'
  | 'loadSpacing'
  | 'deliveryWindow'
  | 'operationalHours'
  | 'orderCancellationLeadTime'
  | 'orderIntakeLeadTime'
  | 'orderChangeLeadTime'
  | 'overbookingAllowance'
  | 'pumpConfiguration'
  | 'trucks'
  | 'loadSpacing'

/** Overrides for entity types whose API path differs from their catalog ID. */
const ENTITY_API_PATH_OVERRIDES: Partial<Record<EntityType, string>> = {
  unloadingMethod: 'unloading-methods',
  deliveryWindow: 'delivery-windows',
  operationalHours: 'operational-hours',
  orderIntakeLeadTime: 'order-intake-lead-times',
  orderCancellationLeadTime: 'order-cancellation-lead-times',
  orderChangeLeadTime: 'order-change-lead-times',
  overbookingAllowance: 'overbooking-allowances',
  pumpConfiguration: 'pump-configurations',
  loadSpacing: 'load-spacing',
}

/** Returns the API path segment for scope/config endpoints given an entity catalog ID. */
export const getEntityApiPath = (entityType: EntityType): string =>
  ENTITY_API_PATH_OVERRIDES[entityType] ?? entityType

export interface Scope {
  /** Scope ID — matches route param / ScopeNavigator scope ID */
  id: string
  /** Display label — e.g. "Sales org. 320" */
  label: string
  /** Optional secondary line — e.g. org unit name for disambiguating same-named scopes */
  subtitle?: string
  /** Whether this scope has overridden values */
  overridden: boolean
  /** Whether this scope has overrides from a different entity */
  otherEntityOverride?: boolean
  /** The hierarchy level of this scope */
  level?: ScopeLevel
}

// --- Scope verify / add types ---

export interface ScopeResultItem {
  /** Database ID — null if scope candidate does not yet exist in DB */
  id: string | null
  parentId?: string
  displayName: string
  displayNumber?: string
  level: ScopeLevel
  scopeData?: Record<string, string>
  /** True if this scope already has overrides for the current entity type */
  isOverridden: boolean
}

export interface VerifyScopeResult {
  found: boolean
  /** List of matching scopes — only present if found */
  results?: ScopeResultItem[]
}

export interface ScopeItem {
  id: string
  parentId?: string
  /** Display name of the scope — e.g. "Heidelberg Materials" */
  displayName: string
  /** Display number / secondary info — e.g. "Sales org. 23" */
  displayNumber?: string
  level?: ScopeLevel
  scopeData?: Record<string, string>
  isOverridden?: boolean
}

// --- EntityCard types (overview page) ---

export interface EntityCardField {
  /** Field display name — rendered as pill text */
  name: string
  /** Whether this field value has been changed — renders orange pill */
  isOverridden?: boolean
}

export interface EntityCardProps {
  /** Card title — e.g. "Load spacing" */
  title: string
  /** Card description — e.g. "Configures the time-based truck options..." */
  description: string
  /** Configured fields to display as pill badges — blue by default, orange if overridden */
  configuredFields: EntityCardField[]
  /** Scope levels at which this entity is applicable — shown as icons */
  applicableLevels?: ScopeLevel[]
  /** Search term to highlight in the description text */
  searchHighlight?: string
  /** Callback when the card body is clicked — navigates to detail */
  onClick?: () => void
  /** Test ID for Cypress */
  'data-cy'?: string
}

// --- Field-level inheritance label (future — types only) ---

/** Inheritance state for individual form fields */
export type FieldInheritanceState =
  | 'inherited'
  | 'setHere'
  | 'inheritsFromDefault'
  | 'valueChanged'
  | 'overriddenValues'
  | 'default'

export interface FieldInheritanceLabelProps {
  state: FieldInheritanceState
  /** Source scope name — used for 'inheritsFromDefault' state */
  sourceName?: string
  /** Callback when clicking the source link */
  onSourceClick?: () => void
  /** Callback when clicking "Country default" link */
  onDefaultClick?: () => void
}

// --- Shared cross-domain types ---

/** Generic override value wrapper from the API */
export interface OverrideValue<T> {
  isOverridden: boolean
  value: T
}

/** Metadata from the API */
export interface MetaResponse {
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}
